/**
 * Service to manage the NEET question results and AI model comparisons
 */
class ResultsService {
  constructor() {
    this.storageKey = 'neetResults';
  }

  /**
   * Get all results from local storage
   * @param {boolean} sort - Whether to sort the results by question number
   * @returns {Array} - Array of result objects
   */
  getAllResults(sort = true) {
    try {
      const results = localStorage.getItem(this.storageKey);
      const parsedResults = results ? JSON.parse(results) : [];
      
      if (sort) {
        return this.sortResultsByQuestionNumber(parsedResults);
      }
      
      return parsedResults;
    } catch (error) {
      console.error('Error retrieving results from local storage:', error);
      return [];
    }
  }

  /**
   * Sort results by question number
   * @param {Array} results - Array of result objects
   * @returns {Array} - Sorted array of result objects
   */
  sortResultsByQuestionNumber(results) {
    return [...results].sort((a, b) => {
      // Convert questionNumber to numbers for proper numerical sorting
      const numA = parseInt(a.questionNumber, 10);
      const numB = parseInt(b.questionNumber, 10);
      
      // Handle cases where questionNumber might not be a number
      if (isNaN(numA) && isNaN(numB)) return 0;
      if (isNaN(numA)) return 1;
      if (isNaN(numB)) return -1;
      
      return numA - numB;
    });
  }

  /**
   * Save a result to local storage
   * @param {Object} result - Result object with AI model answers and solutions
   */
  saveResult(result) {
    try {
      const results = this.getAllResults(false); // Get unsorted results
      
      // Check if result with this ID already exists
      const existingIndex = results.findIndex(r => r.id === result.id);
      
      if (existingIndex >= 0) {
        // Update existing result, preserving any existing properties
        results[existingIndex] = { 
          ...results[existingIndex], 
          ...result,
          // Preserve existing solutions if not provided in the new result
          geminiSolution: result.geminiSolution || results[existingIndex].geminiSolution,
          anthropicSolution: result.anthropicSolution || results[existingIndex].anthropicSolution,
          openaiSolution: result.openaiSolution || results[existingIndex].openaiSolution
        };
      } else {
        // Add new result
        results.push(result);
      }
      
      localStorage.setItem(this.storageKey, JSON.stringify(results));
      return true;
    } catch (error) {
      console.error('Error saving result to local storage:', error);
      return false;
    }
  }

  /**
   * Update a model's answer and solution for a specific question
   * @param {string} questionId - ID of the question
   * @param {string} model - Model name (gemini, anthropic, openai)
   * @param {string} answer - Answer choice (A, B, C, D)
   * @param {string} solution - Solution explanation text
   */
  updateModelAnswerAndSolution(questionId, model, answer, solution) {
    try {
      const modelPropertyMap = {
        'gemini': { answer: 'geminiAnswer', solution: 'geminiSolution' },
        'anthropic': { answer: 'anthropicAnswer', solution: 'anthropicSolution' },
        'openai': { answer: 'openaiAnswer', solution: 'openaiSolution' }
      };
      
      const properties = modelPropertyMap[model.toLowerCase()];
      if (!properties) {
        throw new Error(`Invalid model name: ${model}`);
      }
      
      const results = this.getAllResults(false);
      const existingIndex = results.findIndex(r => r.id === questionId);
      
      if (existingIndex >= 0) {
        results[existingIndex] = {
          ...results[existingIndex],
          [properties.answer]: answer,
          [properties.solution]: solution
        };
        
        localStorage.setItem(this.storageKey, JSON.stringify(results));
        return true;
      }
      
      return false;
    } catch (error) {
      console.error(`Error updating ${model} answer and solution for question ${questionId}:`, error);
      return false;
    }
  }

  /**
   * Update a specific property of a result
   * @param {string} resultId - ID of the result to update
   * @param {string} property - Property to update
   * @param {any} value - New value for the property
   */
  updateResultProperty(resultId, property, value) {
    try {
      const results = this.getAllResults(false); // Get unsorted results
      const existingIndex = results.findIndex(r => r.id === resultId);
      
      if (existingIndex >= 0) {
        results[existingIndex] = { 
          ...results[existingIndex], 
          [property]: value 
        };
        
        localStorage.setItem(this.storageKey, JSON.stringify(results));
        return true;
      }
      
      return false;
    } catch (error) {
      console.error(`Error updating ${property} for result ${resultId}:`, error);
      return false;
    }
  }

  /**
   * Delete a result by ID
   * @param {string} resultId - ID of the result to delete
   */
  deleteResult(resultId) {
    try {
      const results = this.getAllResults(false); // Get unsorted results
      const filteredResults = results.filter(r => r.id !== resultId);
      
      localStorage.setItem(this.storageKey, JSON.stringify(filteredResults));
      return true;
    } catch (error) {
      console.error(`Error deleting result ${resultId}:`, error);
      return false;
    }
  }

  /**
   * Clear all results from local storage
   */
  clearAllResults() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify([]));
      return true;
    } catch (error) {
      console.error('Error clearing results from local storage:', error);
      return false;
    }
  }

  /**
   * Add a question to the results table
   * @param {Object} question - Question object with id, subject, questionNumber, text properties
   */
  addQuestion(question) {
    try {
      // Create new result entry
      const newResult = {
        id: question.id,
        questionNumber: question.questionNumber,
        subject: question.subject,
        question: question.text,
        geminiAnswer: question.geminiAnswer || null,
        geminiSolution: question.geminiSolution || null,
        anthropicAnswer: question.anthropicAnswer || null,
        anthropicSolution: question.anthropicSolution || null,
        openaiAnswer: question.openaiAnswer || null,
        openaiSolution: question.openaiSolution || null,
        actualCorrectAnswer: null,
        options: question.options
      };
      
      return this.saveResult(newResult);
    } catch (error) {
      console.error('Error adding question to results:', error);
      return false;
    }
  }

  /**
   * Update an AI model's answer for a specific question
   * @param {string} questionId - ID of the question
   * @param {string} model - Model name (gemini, anthropic, openai)
   * @param {string} answer - Answer choice (A, B, C, D)
   */
  updateModelAnswer(questionId, model, answer) {
    try {
      const modelPropertyMap = {
        'gemini': 'geminiAnswer',
        'anthropic': 'anthropicAnswer',
        'openai': 'openaiAnswer'
      };
      
      const property = modelPropertyMap[model.toLowerCase()];
      if (!property) {
        throw new Error(`Invalid model name: ${model}`);
      }
      
      return this.updateResultProperty(questionId, property, answer);
    } catch (error) {
      console.error(`Error updating ${model} answer for question ${questionId}:`, error);
      return false;
    }
  }

  /**
   * Update the actual correct answer for a question
   * @param {string} questionId - ID of the question
   * @param {string} answer - Correct answer choice (A, B, C, D)
   */
  updateActualAnswer(questionId, answer) {
    return this.updateResultProperty(questionId, 'actualCorrectAnswer', answer);
  }
}

// Export a singleton instance
export const resultsService = new ResultsService();